import React from 'react';
import { Box, Paper, Typography, Grid, Button, LinearProgress, Tooltip } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { deriveReadiness } from '../dutyOfCare';

function bandColor(pct) {
  if (pct >= 80) return '#10b981';
  if (pct >= 50) return '#f59e0b';
  return '#ef4444';
}

// Compact duty-of-care readiness banner for the top of the business dashboard.
// Aggregates the same profile-completeness signals used on the Compliance page
// and frames them against the employer's travel-risk duty of care (ISO 31030).
export default function DutyOfCareSummary({ users = [], loading = false, onViewDetails }) {
  const { total, avg, atRisk, missingCritical, compliant, readyPct } = deriveReadiness(users);
  const ringColor = bandColor(readyPct);

  const cards = [
    {
      icon: <HealthAndSafetyIcon />,
      label: 'Workforce emergency-ready',
      value: loading ? '…' : `${readyPct}%`,
      sub: loading ? '' : `${compliant} of ${total} ready`,
      color: ringColor,
      progress: loading ? 0 : readyPct,
    },
    {
      icon: <VerifiedUserIcon />,
      label: 'Avg. profile completeness',
      value: loading ? '…' : `${avg}%`,
      color: '#3b82f6',
    },
    {
      icon: <ContactEmergencyIcon />,
      label: 'Missing critical emergency data',
      value: loading ? '…' : missingCritical,
      color: missingCritical > 0 ? '#ef4444' : '#10b981',
      hint: 'Employees missing an emergency contact and/or insurance details — what a first responder needs first.',
    },
    {
      icon: <WarningAmberIcon />,
      label: 'Profiles at risk (<50%)',
      value: loading ? '…' : atRisk,
      color: atRisk > 0 ? '#f59e0b' : '#10b981',
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid #e5e7eb',
        p: 3,
        mb: 3,
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2.5,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: '#32ABF818',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <HealthAndSafetyIcon sx={{ color: '#32ABF8', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>
              Duty of Care Readiness
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              Travel-risk preparedness across your workforce · aligned to ISO 31030
            </Typography>
          </Box>
        </Box>

        {onViewDetails && (
          <Button
            onClick={onViewDetails}
            endIcon={<ArrowForwardIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: '#32ABF8',
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#32ABF812' },
            }}
          >
            View compliance details
          </Button>
        )}
      </Box>

      <Grid container spacing={2}>
        {cards.map((c) => (
          <Grid item xs={12} sm={6} md={3} key={c.label}>
            <Tooltip title={c.hint || ''} arrow disableHoverListener={!c.hint}>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid #EAECF0',
                  bgcolor: '#fff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  cursor: c.hint ? 'help' : 'default',
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    bgcolor: `${c.color}18`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 0.5,
                  }}
                >
                  {React.cloneElement(c.icon, { sx: { color: c.color, fontSize: 20 } })}
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#101828', lineHeight: 1 }}>
                  {c.value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#667085', fontWeight: 500 }}>
                  {c.label}
                </Typography>
                {c.progress !== undefined && (
                  <Box sx={{ mt: 'auto', pt: 1.5 }}>
                    <LinearProgress
                      variant="determinate"
                      value={c.progress}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#F2F4F7',
                        '& .MuiLinearProgress-bar': { bgcolor: c.color, borderRadius: 3 },
                      }}
                    />
                    {c.sub && (
                      <Typography variant="caption" sx={{ color: '#98A2B3', display: 'block', mt: 0.5 }}>
                        {c.sub}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}
